import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { C } from "./utils";
import { ThemeCtx } from "./AppShell";
import { GalleryTile } from "./Dashboard";

const API_BASE = "https://cip.medsphere.net/api";

const openDocTab = (doc, subId) => {
  const base = window.location.origin + window.location.pathname;
  localStorage.setItem(`doc_stateid_${doc.DOCUMENTID}`, "2");
  window.open(`${base}?fdetail=1&doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};

// ── Typing dots ───────────────────────────────────────────────────────────────
function TypingDots({ color }) {
  return(
    <div style={{display:"flex",gap:"4px",alignItems:"center",padding:"6px 2px"}}>
      {[0,1,2].map(i=>(
        <div key={i} style={{width:"6px",height:"6px",borderRadius:"50%",
          background:color,opacity:.5,
          animation:`tdot 1.2s ease-in-out ${i*0.2}s infinite`}}/>
      ))}
      <style>{"@keyframes tdot{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}"}</style>
    </div>
  );
}

// ── Chat bubble ───────────────────────────────────────────────────────────────
function Bubble({ msg, color }) {
  const isUser = msg.ROLE === "user";
  return(
    <div style={{display:"flex",justifyContent:isUser?"flex-end":"flex-start",
      marginBottom:"10px",alignItems:"flex-end",gap:"8px"}}>
      {!isUser&&(
        <div style={{width:"26px",height:"26px",borderRadius:"50%",flexShrink:0,
          background:`linear-gradient(135deg,${color},${color}88)`,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
        </div>
      )}
      <div data-role={isUser?"user":"assistant"}
        style={{maxWidth:isUser?"75%":"100%",padding:"10px 14px",
          fontSize:"13px",lineHeight:1.65,
          borderRadius:isUser?"16px 16px 4px 16px":"4px 16px 16px 16px",
          background:isUser?`linear-gradient(135deg,${color},${color}CC)`:"#F1F5F9",
          color:isUser?"#fff":"#1E293B",
          boxShadow:"0 2px 6px rgba(0,0,0,.07)",overflowX:"auto"}}>
        {isUser
          ? msg.VALUES_OF_CONTENT
          : <div dangerouslySetInnerHTML={{__html:
              `<style>
                .aic p{margin:6px 0;line-height:1.7}
                .aic h1,.aic h2,.aic h3{margin:10px 0 5px;color:#0F172A}
                .aic h3{font-size:13px}.aic h2{font-size:14px}.aic h1{font-size:15px}
                .aic ul,.aic ol{margin:6px 0;padding-left:20px}
                .aic li{margin:3px 0;line-height:1.6}
                .aic hr{border:none;border-top:1px solid #E2E8F0;margin:10px 0}
                .aic table{border-collapse:collapse;width:100%;font-size:12px;margin:8px 0}
                .aic th{background:#E2E8F0;padding:6px 10px;text-align:left;font-weight:600;border:1px solid #CBD5E1}
                .aic td{padding:6px 10px;border:1px solid #E2E8F0;vertical-align:top}
                .aic tr:nth-child(even) td{background:#F8FAFC}
                .aic strong{font-weight:600;color:#0F172A}
                .aic code{background:#F1F5F9;padding:1px 5px;border-radius:4px;font-size:11px;font-family:monospace}
              </style><div class="aic">${msg.VALUES_OF_CONTENT}</div>`
            }}/>
        }
      </div>
    </div>
  );
}

// ── AI Chat Overlay — self-contained, used by AppShell ───────────────────────
export function AIChatOverlay({ history, thread, setThread, user, subproject,
  color, width=680, onClose }) {

  const [msgs,      setMsgs]      = useState(history||[]);
  const [followUp,  setFollowUp]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [chatWidth, setChatWidth] = useState(width);
  const chatRef = useRef(null);
  const isMobile = window.innerWidth < 768;

  // Sync when history prop updates (first response arrives)
  useEffect(()=>{ setMsgs(history||[]); },[history]);

  const scrollToFirst = () => setTimeout(()=>{
    if(!chatRef.current) return;
    const bubbles = chatRef.current.querySelectorAll("[data-role=assistant]");
    const last = bubbles[bubbles.length-1];
    if(last) last.scrollIntoView({behavior:"smooth",block:"start"});
  },100);

  const sendFollowUp = async () => {
    const q = followUp.trim();
    if(!q||loading) return;
    setLoading(true);
    setMsgs(h=>[...h,{ROLE:"user",VALUES_OF_CONTENT:q}]);
    setFollowUp("");
    setTimeout(()=>{ if(chatRef.current) chatRef.current.scrollTop=chatRef.current.scrollHeight; },50);
    try {
      const fd = new FormData();
      fd.append("review", q);
      fd.append("subprojectid", subproject.SUBPROJECTID);
      fd.append("userid", user.USERID);
      fd.append("mode", "ai");
      if(thread) fd.append("threadid", thread);
      const r = await fetch(`${API_BASE}/Vector_search`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      const res = await r.json();
      if(res.STATUS===1){
        if(res.THREAD) setThread(res.THREAD);
        if(res.COMPLETION){
          setMsgs(h=>[...h,{ROLE:"assistant",VALUES_OF_CONTENT:res.COMPLETION}]);
          scrollToFirst();
        }
      }
    } catch(e){ console.error(e); }
    setLoading(false);
  };

  const Btn = ({onClick, title, children, red}) => (
    <button onClick={onClick} title={title}
      style={{width:"26px",height:"26px",borderRadius:"6px",cursor:"pointer",
        border:red?"1px solid #FECACA":`1px solid ${C.border}`,
        background:"#fff",fontSize:"14px",color:red?"#EF4444":C.muted,fontWeight:"600",
        display:"flex",alignItems:"center",justifyContent:"center"}}>
      {children}
    </button>
  );

  return(
    <>
      {/* Minimized pill — desktop only */}
      {!isMobile&&<div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:900,
        width:"220px",height:"48px",borderRadius:"24px",overflow:"hidden",
        boxShadow:"0 8px 24px rgba(0,0,0,0.2)",cursor:"pointer",
        display:minimized?"flex":"none",alignItems:"center",padding:"0 14px",gap:"8px",
        background:`linear-gradient(135deg,${color},${color}BB)`}}
        onClick={()=>setMinimized(false)}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="#fff" strokeWidth="2" strokeLinecap="round">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
        <span style={{flex:1,fontSize:"13px",fontWeight:"600",color:"#fff"}}>Research Assistant</span>
        <button onClick={e=>{e.stopPropagation();onClose();}}
          style={{background:"none",border:"none",color:"rgba(255,255,255,0.8)",
            cursor:"pointer",fontSize:"18px",padding:0,lineHeight:1}}>×</button>
      </div>}

      {/* Full overlay */}
      <div style={{position:"fixed",
        top: isMobile?"48px":"58px",
        right: isMobile?0:"20px",
        left: isMobile?0:"auto",
        bottom: isMobile?"56px":0,
        width: isMobile?"100%":chatWidth+"px",
        height: isMobile?"auto":"calc(100vh - 78px)",
        zIndex:900,
        borderRadius: isMobile?"0":"16px",
        overflow:"hidden",
        boxShadow:"0 20px 60px rgba(0,0,0,0.25)",
        display:minimized?"none":"flex",flexDirection:"column",background:"#fff",
        transition:"width .2s"}}>

      {/* Header */}
      <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"8px",
        padding:"10px 12px",borderBottom:`1px solid ${C.border}`,
        background:`linear-gradient(135deg,${color}0F,${color}06)`}}>
        <div style={{width:"26px",height:"26px",borderRadius:"8px",flexShrink:0,
          background:`linear-gradient(135deg,${color},${color}BB)`,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2" strokeLinecap="round">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
        </div>
        <span style={{flex:1,fontSize:"13px",fontWeight:"700",color:"#0F172A"}}>Research Assistant</span>
        <div style={{display:"flex",gap:"4px"}}>
          {!isMobile&&<Btn onClick={()=>setChatWidth(w=>Math.max(320,w-120))} title="Shrink">‹</Btn>}
          {!isMobile&&<Btn onClick={()=>setChatWidth(w=>Math.min(1200,w+120))} title="Expand">›</Btn>}
          {!isMobile&&<Btn onClick={()=>setMinimized(true)} title="Minimize">↓</Btn>}
          <Btn onClick={onClose} title="Close" red>×</Btn>
        </div>
      </div>

      {/* Messages */}
      <div ref={chatRef} style={{flex:1,overflowY:"auto",overflowX:"hidden",
        padding:"14px",display:"flex",flexDirection:"column",minHeight:0}}>
        {msgs.map((m,i)=><Bubble key={i} msg={m} color={color}/>)}
        {(loading||(msgs.length===1&&msgs[0].ROLE==="user"))&&(
          <div style={{display:"flex",gap:"8px",alignItems:"flex-end",marginBottom:"10px"}}>
            <div style={{width:"26px",height:"26px",borderRadius:"50%",flexShrink:0,
              background:`linear-gradient(135deg,${color},${color}88)`,
              display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div style={{padding:"10px 14px",borderRadius:"4px 16px 16px 16px",background:"#F1F5F9"}}>
              <TypingDots color={color}/>
            </div>
          </div>
        )}
      </div>

      {/* Follow-up */}
      <div style={{flexShrink:0,padding:"10px 12px",borderTop:`1px solid ${C.border}`}}>
        {thread&&<div style={{fontSize:"10px",color:C.hint,marginBottom:"4px"}}>Continuing conversation</div>}
        <div style={{display:"flex",gap:"6px",alignItems:"flex-end"}}>
          <textarea value={followUp} onChange={e=>setFollowUp(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey&&!loading){ e.preventDefault(); sendFollowUp(); }}}
            rows={2} placeholder="Ask a follow-up…" disabled={loading}
            style={{flex:1,padding:"8px 10px",borderRadius:"10px",resize:"none",
              border:`1.5px solid ${followUp?color:C.border}`,outline:"none",
              fontFamily:"inherit",fontSize:"12px",lineHeight:1.5,transition:"all .15s"}}/>
          <button onClick={sendFollowUp} disabled={!followUp.trim()||loading}
            style={{width:"34px",height:"34px",borderRadius:"9px",border:"none",flexShrink:0,
              background:followUp.trim()&&!loading?color:"#E2E8F0",
              cursor:followUp.trim()&&!loading?"pointer":"not-allowed",
              display:"flex",alignItems:"center",justifyContent:"center"}}>
            {loading
              ? <div style={{width:"12px",height:"12px",border:"2px solid #fff",
                  borderTopColor:"transparent",borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke={followUp.trim()?"#fff":C.muted} strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
            }
            <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

// ── VectorSearch — documents only, calls onAIResponse when mode=ai ─────────────
export default function VectorSearch({ user, subproject, initialQuery="", onAIResponse=null }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;

  const [docs,         setDocs]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [hasMore,      setHasMore]      = useState(false);
  const [lastQuery,    setLastQuery]    = useState("");
  const [responseMode, setResponseMode] = useState(null);
  const PAGE    = 20;
  const pageRef = useRef(0);
  const didInit = useRef(false);

  const doSearch = useCallback(async (q, reset=true) => {
    const qTrim = (q||"").trim();
    if(!qTrim||!subproject) return;
    setLoading(true);
    setLastQuery(qTrim);
    if(reset){ setDocs([]); pageRef.current=0; }
    try {
      const fd = new FormData();
      fd.append("review",       qTrim);
      fd.append("subprojectid", subproject.SUBPROJECTID);
      fd.append("userid",       user.USERID);
      const r   = await fetch(`${API_BASE}/Vector_search`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      const res = await r.json();
      if(res.STATUS===1){
        setResponseMode(res.MODE==="ai"?"ai":"basic");
        if(Array.isArray(res.DATA)){
          if(reset) setDocs(res.DATA);
          else      setDocs(prev=>[...prev,...res.DATA]);
          setHasMore(res.DATA.length===PAGE);
          pageRef.current = reset?1:pageRef.current+1;
        }
        if(res.MODE==="ai"&&res.COMPLETION&&onAIResponse){
          onAIResponse({
            history:[
              {ROLE:"user",     VALUES_OF_CONTENT:qTrim},
              {ROLE:"assistant",VALUES_OF_CONTENT:res.COMPLETION}
            ],
            thread: res.THREAD||null
          });
        }
      }
    } catch(e){ console.error(e); }
    setLoading(false);
  },[subproject, user, onAIResponse]);

  useEffect(()=>{
    if(didInit.current) return;
    didInit.current = true;
    if(initialQuery.trim()) doSearch(initialQuery, true);
  // eslint-disable-next-line
  },[]);

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#F8FAFC"}}>
      {/* Results bar */}
      <div style={{flexShrink:0,padding:"10px 20px",borderBottom:`1px solid ${C.border}`,
        background:"#fff",display:"flex",alignItems:"center",gap:"10px",minHeight:"48px"}}>
        {lastQuery?(
          <div style={{flex:1,fontSize:"12px",color:C.muted}}>
            <span style={{fontWeight:"700",color:"#0F172A"}}>{docs.length}{hasMore?"+":""}</span>
            {" result"}{docs.length!==1?"s":""}{" for "}
            <span style={{fontStyle:"italic",color}}>&ldquo;{lastQuery}&rdquo;</span>
            {responseMode&&(
              <span style={{marginLeft:"8px",fontSize:"10px",fontWeight:"700",
                padding:"2px 7px",borderRadius:"20px",
                background:responseMode==="ai"?`${color}15`:"#F1F5F9",
                color:responseMode==="ai"?color:C.muted}}>
                {responseMode==="ai"?"✦ AI":"🔍 Search"}
              </span>
            )}
          </div>
        ):(
          <div style={{flex:1,fontSize:"13px",color:C.muted}}>
            {loading?"Searching…":"Type in the search bar above and press Enter"}
          </div>
        )}
      </div>

      {/* Document grid */}
      <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
        {loading&&docs.length===0&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",
            justifyContent:"center",height:"200px",gap:"16px"}}>
            <div style={{position:"relative",width:"48px",height:"48px"}}>
              <div style={{position:"absolute",inset:0,borderRadius:"50%",border:`3px solid ${color}20`}}/>
              <div style={{position:"absolute",inset:0,borderRadius:"50%",
                border:`3px solid transparent`,borderTopColor:color,
                animation:"spin .9s linear infinite"}}/>
              <div style={{position:"absolute",inset:"8px",borderRadius:"50%",
                border:`3px solid transparent`,borderTopColor:color,opacity:.5,
                animation:"spin .7s linear infinite reverse"}}/>
              <div style={{position:"absolute",inset:0,display:"flex",
                alignItems:"center",justifyContent:"center"}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke={color} strokeWidth="2" strokeLinecap="round"
                  style={{animation:"pulse 1.5s ease-in-out infinite"}}>
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
              </div>
            </div>
            <div style={{fontSize:"13px",color:C.muted,fontWeight:"500",
              animation:"pulse 1.5s ease-in-out infinite"}}>Searching intelligence…</div>
            <style>{"@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}"}</style>
          </div>
        )}
        {!loading&&!lastQuery&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",
            justifyContent:"center",height:"60%",gap:"14px"}}>
            <svg width="52" height="52" viewBox="0 0 24 24" fill="none"
              stroke={C.border} strokeWidth="1" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
              <path d="M11 8v6M8 11h6"/>
            </svg>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:"15px",fontWeight:"600",color:"#475569",marginBottom:"6px"}}>
                Search your pharma intelligence
              </div>
              <div style={{fontSize:"12px",color:C.hint,lineHeight:1.7}}>
                Type in the search bar above and press <b>Enter</b>
              </div>
            </div>
          </div>
        )}
        {!loading&&docs.length===0&&lastQuery&&(
          <div style={{textAlign:"center",padding:"60px",color:C.muted}}>
            <div style={{fontSize:"14px",fontWeight:"600",color:"#475569",marginBottom:"4px"}}>No documents found</div>
            <div style={{fontSize:"12px"}}>Try different keywords</div>
          </div>
        )}
        {docs.length>0&&(
          <div style={{display:"grid",
            gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"12px"}}>
            {docs.map(doc=>(
              <GalleryTile key={doc.DOCUMENTID} doc={doc} showDocType={true}
                onOpen={d=>openDocTab(d,subproject?.SUBPROJECTID)}/>
            ))}
          </div>
        )}
        {hasMore&&!loading&&(
          <div style={{textAlign:"center",marginTop:"20px"}}>
            <button onClick={()=>doSearch(lastQuery,false)}
              style={{padding:"9px 28px",borderRadius:"8px",
                border:`1px solid ${C.border}`,background:"#fff",
                color,fontSize:"13px",fontWeight:"500",cursor:"pointer"}}>
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}